import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Grid, Card, CardMedia, Box, Typography, Button, TextField, Avatar, Menu, MenuItem, 
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, 
    InputLabel, Select, Table, TableBody, TableCell, TableContainer, TableHead, 
    TableRow, Paper, Chip, Autocomplete, Tooltip, Slider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import MapIcon from '@mui/icons-material/Map';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import WarningIcon from '@mui/icons-material/Warning';
import SettingsIcon from '@mui/icons-material/Settings';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import LinearProgress from '@mui/material/LinearProgress';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import styles from './Flights.module.css';
import Logo from '../../assets/logo_white.png';
import AvatarImage from '../../assets/PigeonsCatalog/Avatar.png';
import customIcon1 from '../../assets/PigeonsCatalog/pearson_catalog.svg';
import customIcon2 from '../../assets/PigeonsCatalog/wing_catalog.svg';
import customIcon3 from '../../assets/PigeonsCatalog/pair_catalog.svg';
import customIcon4 from '../../assets/PigeonsCatalog/genealogy_catalog.svg';
import customIcon5 from '../../assets/PigeonsCatalog/flight_catalog.svg';
import pigeonFlightImage from '../../assets/flying_pigeon.png';

const Flights = () => {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [flights, setFlights] = useState([]);
    const [addFlightOpen, setAddFlightOpen] = useState(false);
    const [editFlightOpen, setEditFlightOpen] = useState(false);
    const [editingFlight, setEditingFlight] = useState(null);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [mapDialogOpen, setMapDialogOpen] = useState(false);
    const [simulatorOpen, setSimulatorOpen] = useState(false);
    const [riskAnalysisOpen, setRiskAnalysisOpen] = useState(false);
    const [homeLocationDialogOpen, setHomeLocationDialogOpen] = useState(false);
    const [selectedFlightForSim, setSelectedFlightForSim] = useState(null);
    const [mapCenter, setMapCenter] = useState({ lat: 52.2297, lng: 21.0122 }); // Warszawa domyślnie
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [homeLocation, setHomeLocation] = useState({ lat: 52.2297, lng: 21.0122 }); // Lokalizacja gołębnika
    const [homeLocationName, setHomeLocationName] = useState('Gołębnik');
    const [homeLocationSearch, setHomeLocationSearch] = useState('');
    const [weatherData, setWeatherData] = useState(null);
    const [weatherLoading, setWeatherLoading] = useState(false);
    const [simulatorSpeed, setSimulatorSpeed] = useState(1); // Prędkość symulacji
    const [simulatorProgress, setSimulatorProgress] = useState(0); // Postęp symulacji
    const [simulatorPlaying, setSimulatorPlaying] = useState(false);
    const [pigeonsList, setPigeonsList] = useState([]);
    const [selectedPigeonId, setSelectedPigeonId] = useState('');
    const [simReleaseDateTime, setSimReleaseDateTime] = useState('');
    const [simArrivalDateTime, setSimArrivalDateTime] = useState('');
    const [flightForm, setFlightForm] = useState({
        name: '',
        releaseDate: '',
        releaseLocation: '',
        releaseLat: '',
        releaseLng: '',
        distance: '',
        category: '',
        pigeonsCount: '',
        position: '',
        ringNumber: '',
        speed: '',
        rankingPosition: '',
        coefficient: '',
        source: 'manual',
        status: 'planned', // 'planned' lub 'completed'
        attachment: null,
        notes: ''
    });
    const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'planned', 'completed'

    // Wczytywanie lotów z localStorage
    useEffect(() => {
        const savedFlights = localStorage.getItem('flightsData');
        if (savedFlights) {
            try {
                setFlights(JSON.parse(savedFlights));
            } catch (error) {
                console.error('Błąd wczytywania lotów:', error);
            }
        }
    }, []);

    useEffect(() => {
        const savedPigeons = localStorage.getItem('pigeonsData');
        if (savedPigeons) {
            try {
                const parsed = JSON.parse(savedPigeons);
                if (Array.isArray(parsed)) {
                    setPigeonsList(parsed);
                    if (parsed.length > 0) {
                        setSelectedPigeonId(parsed[0].ID || parsed[0].id || '');
                    }
                }
            } catch (error) {
                console.error('Błąd wczytywania gołębi:', error);
            }
        }
    }, []);

    useEffect(() => {
        const savedHomeLocation = localStorage.getItem('homeLocation');
        if (savedHomeLocation) {
            try {
                const parsed = JSON.parse(savedHomeLocation);
                if (parsed?.lat && parsed?.lng) {
                    setHomeLocation({ lat: parsed.lat, lng: parsed.lng });
                }
                if (parsed?.name) {
                    setHomeLocationName(parsed.name);
                }
            } catch (error) {
                console.error('Błąd wczytywania lokalizacji gołębnika:', error);
            }
        }
    }, []);

    // Zapisywanie lotów do localStorage
    useEffect(() => {
        if (flights.length > 0) {
            localStorage.setItem('flightsData', JSON.stringify(flights));
        }
    }, [flights]);

    // Pobierz dane pogodowe przy otwieraniu analizy ryzyka
    useEffect(() => {
        if (riskAnalysisOpen && selectedFlightForSim && selectedFlightForSim.releaseLat && selectedFlightForSim.releaseLng) {
            // Resetuj dane i pobierz nowe
            setWeatherData(null);
            setWeatherLoading(true);
            const lat = parseFloat(selectedFlightForSim.releaseLat);
            const lng = parseFloat(selectedFlightForSim.releaseLng);
            const date = selectedFlightForSim.releaseDate;
            
            fetchWeatherData(lat, lng, date);
        }
    }, [riskAnalysisOpen, selectedFlightForSim?.id]);

    // Przygotowanie i reset symulatora przy zmianie lotu/otwarciu
    useEffect(() => {
        if (simulatorOpen) {
            setSimulatorProgress(0);
            setSimulatorSpeed(1);
            setSimulatorPlaying(false);
            const initialRelease = selectedFlightForSim?.releaseDate
                ? `${selectedFlightForSim.releaseDate}T08:00`
                : '';
            setSimReleaseDateTime(initialRelease);
            setSimArrivalDateTime('');
            if (selectedFlightForSim?.releaseLat && selectedFlightForSim?.releaseLng) {
                fetchWeatherData(
                    parseFloat(selectedFlightForSim.releaseLat),
                    parseFloat(selectedFlightForSim.releaseLng),
                    selectedFlightForSim.releaseDate
                );
            }
        }
    }, [simulatorOpen, selectedFlightForSim?.id]);

    // Animacja postępu lotu
    useEffect(() => {
        if (!simulatorOpen || !simulatorPlaying) return undefined;
        const releaseTimeMs = simReleaseDateTime ? new Date(simReleaseDateTime).getTime() : null;
        const arrivalTimeMs = simArrivalDateTime ? new Date(simArrivalDateTime).getTime() : null;
        const providedDurationMinutes = releaseTimeMs && arrivalTimeMs && arrivalTimeMs > releaseTimeMs
            ? (arrivalTimeMs - releaseTimeMs) / (1000 * 60)
            : null;
        if (!providedDurationMinutes) {
            setSimulatorPlaying(false);
            return undefined;
        }
        const totalFlightMinutes = providedDurationMinutes;

        const timer = setInterval(() => {
            setSimulatorProgress((prev) => {
                // x1 = 1 minuta lotu na 1 sekundę animacji.
                const progressStep = (simulatorSpeed * 100) / Math.max(1, totalFlightMinutes * 4);
                const next = Math.min(100, prev + progressStep);
                if (next >= 100) {
                    setSimulatorPlaying(false);
                }
                return next;
            });
        }, 100);

        return () => clearInterval(timer);
    }, [simulatorOpen, simulatorPlaying, simulatorSpeed, simReleaseDateTime, simArrivalDateTime, selectedFlightForSim?.distance, selectedFlightForSim?.speed]);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        setAnchorEl(null);
        navigate('/');
    };

    const handleClick = (index, name) => {
        return () => {
            switch(index) {
                case 0: // Panel hodowcy
                    navigate('/panel');
                    break;
                case 1: // Gołębie
                    navigate('/pigeons-new');
                    break;
                case 2: // Rodowody
                    navigate('/pedigree');
                    break;
                case 3: // Parowanie
                    navigate('/pairing');
                    break;
                case 4: // Loty
                    // Już jesteśmy tutaj
                    break;
                default:
                    break;
            }
        };
    };

    const iconLabels = [
        'Panel hodowcy',
        'Gołębie',
        'Rodowody',
        'Parowanie',
        'Loty'
    ];

    const icons = [customIcon1, customIcon2, customIcon4, customIcon3, customIcon5];

    const categories = ['Młode', 'Stare', 'A', 'B', 'C', 'M', 'Maraton', 'One Loft'];
    const sources = ['manual', 'PDF import', 'CSV import', 'ESK', 'API', 'Federacja'];

    const handleAddFlight = () => {
        setAddFlightOpen(true);
        setFlightForm({
            name: '',
            releaseDate: '',
            releaseLocation: '',
            releaseLat: '',
            releaseLng: '',
            distance: '',
            category: '',
            pigeonsCount: '',
            position: '',
            ringNumber: '',
            speed: '',
            rankingPosition: '',
            coefficient: '',
            source: 'manual',
            attachment: null,
            notes: ''
        });
    };

    const handleCloseAddFlight = () => {
        setAddFlightOpen(false);
        setFlightForm({
            name: '',
            releaseDate: '',
            releaseLocation: '',
            releaseLat: '',
            releaseLng: '',
            distance: '',
            category: '',
            pigeonsCount: '',
            position: '',
            ringNumber: '',
            speed: '',
            rankingPosition: '',
            coefficient: '',
            source: 'manual',
            status: 'planned',
            attachment: null,
            notes: ''
        });
    };

    const handleSaveFlight = () => {
        if (!flightForm.name || !flightForm.releaseDate || !flightForm.distance || !flightForm.category) {
            alert('Wypełnij wszystkie wymagane pola');
            return;
        }

        const newFlight = {
            id: editingFlight ? editingFlight.id : Date.now(),
            ...flightForm,
            distance: parseFloat(flightForm.distance) || 0,
            pigeonsCount: parseInt(flightForm.pigeonsCount) || 0,
            position: parseInt(flightForm.position) || null,
            speed: parseFloat(flightForm.speed) || null,
            rankingPosition: parseInt(flightForm.rankingPosition) || null,
            coefficient: parseFloat(flightForm.coefficient) || null,
            releaseLat: parseFloat(flightForm.releaseLat) || null,
            releaseLng: parseFloat(flightForm.releaseLng) || null,
            createdAt: editingFlight ? editingFlight.createdAt : new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        if (editingFlight) {
            setFlights(prev => prev.map(f => f.id === editingFlight.id ? newFlight : f));
            setEditFlightOpen(false);
        } else {
            setFlights(prev => [...prev, newFlight]);
            setAddFlightOpen(false);
        }
        
        handleCloseAddFlight();
    };

    const handleEditFlight = (flight) => {
        setEditingFlight(flight);
        setFlightForm({
            name: flight.name || '',
            releaseDate: flight.releaseDate || '',
            releaseLocation: flight.releaseLocation || '',
            releaseLat: flight.releaseLat?.toString() || '',
            releaseLng: flight.releaseLng?.toString() || '',
            distance: flight.distance?.toString() || '',
            category: flight.category || '',
            pigeonsCount: flight.pigeonsCount?.toString() || '',
            position: flight.position?.toString() || '',
            ringNumber: flight.ringNumber || '',
            speed: flight.speed?.toString() || '',
            rankingPosition: flight.rankingPosition?.toString() || '',
            coefficient: flight.coefficient?.toString() || '',
            source: flight.source || 'manual',
            attachment: flight.attachment || null,
            notes: flight.notes || ''
        });
        setEditFlightOpen(true);
    };

    const handleDeleteFlight = (flightId) => {
        if (window.confirm('Czy na pewno chcesz usunąć ten lot?')) {
            setFlights(prev => prev.filter(f => f.id !== flightId));
        }
    };

    // Obliczanie statystyk
    const calculateStats = () => {
        const uniqueFlights = new Set(flights.map(f => f.id)).size;
        const top1Count = flights.filter(f => f.position === 1).length;
        const top50Count = flights.filter(f => f.rankingPosition && f.rankingPosition <= 50).length;
        const totalFlights = flights.length;
        
        return {
            totalFlights,
            uniqueFlights,
            top1Count,
            top50Count
        };
    };

    const stats = calculateStats();

    // Obliczanie dystansu z GPS (funkcja pomocnicza)
    const calculateDistance = (lat1, lng1, lat2, lng2) => {
        // Implementacja formuły Haversine
        const R = 6371; // Promień Ziemi w km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLng = (lng2 - lng1) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                  Math.sin(dLng/2) * Math.sin(dLng/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c;
    };

    // Geocoding - konwersja adresu na współrzędne (OpenStreetMap Nominatim - darmowe)
    const handleGeocode = async (address) => {
        if (!address) return;
        try {
            const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`, {
                headers: {
                    'User-Agent': 'PigeonsApp/1.0'
                }
            });
            const data = await response.json();
            if (data && data.length > 0) {
                const lat = parseFloat(data[0].lat);
                const lng = parseFloat(data[0].lon);
                setMapCenter({ lat, lng });
                setSelectedLocation({ lat, lng });
                // Oblicz dystans jeśli mamy lokalizację gołębnika
                let distance = '';
                if (homeLocation.lat && homeLocation.lng) {
                    const dist = calculateDistance(homeLocation.lat, homeLocation.lng, lat, lng);
                    distance = dist.toFixed(2);
                }
                setFlightForm({
                    ...flightForm,
                    releaseLat: lat.toString(),
                    releaseLng: lng.toString(),
                    distance: distance || flightForm.distance
                });
            } else {
                alert('Nie znaleziono lokalizacji. Spróbuj bardziej szczegółowego adresu.');
            }
        } catch (error) {
            console.error('Błąd geocodingu:', error);
            alert('Błąd podczas wyszukiwania lokalizacji. Spróbuj ponownie.');
        }
    };

    // Naprawienie ikon Leaflet (domyślne ikony nie działają bez dodatkowej konfiguracji)
    useEffect(() => {
        delete L.Icon.Default.prototype._getIconUrl;
        L.Icon.Default.mergeOptions({
            iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
            iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
            shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
        });
    }, []);

    // Obsługa kliknięcia na mapie (Leaflet)
    const handleMapClick = (e) => {
        const lat = e.latlng.lat;
        const lng = e.latlng.lng;
        setSelectedLocation({ lat, lng });
        setFlightForm({
            ...flightForm,
            releaseLat: lat.toString(),
            releaseLng: lng.toString()
        });
        // Oblicz dystans
        if (homeLocation.lat && homeLocation.lng) {
            const dist = calculateDistance(homeLocation.lat, homeLocation.lng, lat, lng);
            setFlightForm({
                ...flightForm,
                releaseLat: lat.toString(),
                releaseLng: lng.toString(),
                distance: dist.toFixed(2)
            });
        }
    };

    // Komponent do obsługi kliknięć na mapie
    const MapClickHandler = ({ onClick }) => {
        useMapEvents({
            click: onClick,
        });
        return null;
    };

    // Symulator lotu - obliczanie czasu przelotu i wizualizacja
    const simulateFlight = (flight) => {
        if (!flight.releaseLat || !flight.releaseLng || !flight.distance) {
            return null;
        }
        
        const avgSpeed = flight.speed || 1200; // m/min domyślnie
        const distanceM = flight.distance * 1000; // km na metry
        const timeMinutes = distanceM / avgSpeed;
        const timeHours = Math.floor(timeMinutes / 60);
        const timeMins = Math.floor(timeMinutes % 60);
        
        return {
            estimatedTime: `${timeHours}h ${timeMins}min`,
            timeMinutes,
            estimatedSpeed: avgSpeed,
            distance: flight.distance,
            route: [
                { lat: parseFloat(flight.releaseLat), lng: parseFloat(flight.releaseLng) },
                homeLocation
            ]
        };
    };

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    const getAnimatedWeatherSnapshot = (baseWeather, progress) => {
        if (!baseWeather || baseWeather.error) return null;
        const phase = (progress / 100) * Math.PI * 2;
        const windSpeed = Math.max(0, Math.round(baseWeather.windSpeed + Math.sin(phase * 1.4) * 3));
        const windGusts = Math.max(windSpeed, Math.round((baseWeather.windGusts || windSpeed) + Math.cos(phase * 1.7) * 4));
        const cloudCover = clamp(Math.round(baseWeather.cloudCover + Math.sin(phase * 0.8) * 12), 0, 100);
        const temperature = Math.round(baseWeather.temperature + Math.cos(phase * 1.1) * 1.5);
        const humidity = clamp(Math.round(baseWeather.humidity + Math.sin(phase * 0.7) * 6), 20, 100);
        const visibility = baseWeather.visibility
            ? Math.max(1, Math.round(baseWeather.visibility + Math.cos(phase * 0.9) * 1.2))
            : null;

        return {
            ...baseWeather,
            windSpeed,
            windGusts,
            cloudCover,
            temperature,
            humidity,
            visibility
        };
    };

    const formatDuration = (minutes) => {
        const safeMinutes = Math.max(0, Math.round(minutes || 0));
        const hours = Math.floor(safeMinutes / 60);
        const mins = safeMinutes % 60;
        return `${hours}h ${mins}min`;
    };

    const getWindFieldPoints = (start, end, weatherSnapshot) => {
        if (!start || !end || !weatherSnapshot) return [];
        const directionRad = ((weatherSnapshot.windDirection || 0) * Math.PI) / 180;
        const points = [];
        const segments = 9;
        for (let i = 0; i <= segments; i += 1) {
            const ratio = i / segments;
            const baseLat = start.lat + (end.lat - start.lat) * ratio;
            const baseLng = start.lng + (end.lng - start.lng) * ratio;
            const spread = 0.08;
            points.push({
                lat: baseLat + Math.sin(ratio * Math.PI * 2) * spread,
                lng: baseLng + Math.cos(ratio * Math.PI * 2) * spread,
                angle: weatherSnapshot.windDirection || 0,
                speed: weatherSnapshot.windSpeed || 0,
                color: weatherSnapshot.windSpeed > 25 ? '#e53935' : weatherSnapshot.windSpeed > 15 ? '#fb8c00' : '#43a047',
                key: `wind-${i}-${Math.round(directionRad * 100)}`
            });
        }
        return points;
    };

    const pigeon3DIcon = L.divIcon({
        className: 'pigeon-3d-marker',
        html: `
            <div style="position:relative; width:52px; height:52px; transform:perspective(120px) rotateX(12deg);">
                <div style="position:absolute; bottom:2px; left:8px; width:24px; height:8px; border-radius:50%; background:rgba(0,0,0,0.25); filter:blur(2px);"></div>
                <div style="position:absolute; top:2px; left:2px; width:48px; height:48px; border-radius:50%; background:radial-gradient(circle at 35% 30%, #f5f5f5 0%, #cfd8dc 55%, #90a4ae 100%); display:flex; align-items:center; justify-content:center; box-shadow:0 6px 14px rgba(0,0,0,0.28); overflow:hidden;">
                    <img src="${pigeonFlightImage}" alt="Pigeon" style="width:46px; height:46px; object-fit:contain;" />
                </div>
            </div>
        `,
        iconSize: [52, 52],
        iconAnchor: [26, 34]
    });

    // Pobieranie danych pogodowych z Open-Meteo API (darmowe, bez klucza API)
    const fetchWeatherData = async (lat, lng, date) => {
        setWeatherLoading(true);
        try {
            // Open-Meteo API - darmowe, bez klucza API
            const today = new Date();
            const targetDate = date ? new Date(date) : today;
            const dateStr = targetDate.toISOString().split('T')[0];
            
            // Pobierz aktualne warunki pogodowe
            const response = await fetch(
                `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,relative_humidity_2m,apparent_temperature,is_day,precipitation,rain,showers,snowfall,weather_code,cloud_cover,pressure_msl,surface_pressure,wind_speed_10m,wind_direction_10m,wind_gusts_10m,visibility&timezone=auto&forecast_days=1`
            );
            
            if (!response.ok) {
                throw new Error('Błąd pobierania danych pogodowych');
            }
            
            const data = await response.json();
            const current = data.current;
            
            // Mapowanie kodów pogodowych WMO na opisy
            const weatherCodes = {
                0: 'Czyste niebo',
                1: 'Przeważnie bezchmurnie',
                2: 'Częściowo zachmurzone',
                3: 'Zachmurzone',
                45: 'Mgła',
                48: 'Osadzająca się mgła',
                51: 'Lekka mżawka',
                53: 'Umiarkowana mżawka',
                55: 'Gęsta mżawka',
                56: 'Lekka zamarzająca mżawka',
                57: 'Gęsta zamarzająca mżawka',
                61: 'Lekki deszcz',
                63: 'Umiarkowany deszcz',
                65: 'Silny deszcz',
                66: 'Lekki zamarzający deszcz',
                67: 'Silny zamarzający deszcz',
                71: 'Lekki śnieg',
                73: 'Umiarkowany śnieg',
                75: 'Silny śnieg',
                77: 'Ziarna śniegu',
                80: 'Lekkie przelotne opady deszczu',
                81: 'Umiarkowane przelotne opady deszczu',
                82: 'Silne przelotne opady deszczu',
                85: 'Lekkie przelotne opady śniegu',
                86: 'Silne przelotne opady śniegu',
                95: 'Burza',
                96: 'Burza z gradem',
                99: 'Silna burza z gradem'
            };
            
            const weatherDescription = weatherCodes[current.weather_code] || 'Nieznane';
            
            // Obliczanie wpływu wiatru na lot
            let windImpact = 'neutralny';
            let windImpactDescription = '';
            if (current.wind_speed_10m > 20) {
                windImpact = 'negatywny';
                windImpactDescription = 'Silny wiatr może znacznie utrudnić lot i zwiększyć zużycie energii';
            } else if (current.wind_speed_10m > 15) {
                windImpact = 'negatywny';
                windImpactDescription = 'Umiarkowany wiatr może wpłynąć na czas przelotu';
            } else if (current.wind_speed_10m < 5) {
                windImpact = 'pozytywny';
                windImpactDescription = 'Słaby wiatr - optymalne warunki dla lotu';
            } else {
                windImpactDescription = 'Umiarkowany wiatr - normalne warunki';
            }
            
            // Kierunek wiatru w stopniach na nazwę
            const windDirections = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
            const windDirectionName = windDirections[Math.round(current.wind_direction_10m / 22.5) % 16];
            
            // Ocena warunków ogólnych (spójna z analizą ryzyka)
            let conditionsRating = 'Dobre';
            let riskFromWeather = 0;
            
            // Analiza kodu pogodowego
            if (current.weather_code >= 95 && current.weather_code <= 99) {
                // Burze - bardzo złe
                conditionsRating = 'Bardzo złe';
                riskFromWeather = 35;
            } else if (current.weather_code >= 71 && current.weather_code <= 77) {
                // Śnieg - bardzo złe
                conditionsRating = 'Bardzo złe';
                riskFromWeather = 30;
            } else if (current.weather_code >= 61 && current.weather_code <= 67) {
                // Deszcz - złe
                conditionsRating = 'Złe';
                riskFromWeather = 25;
            } else if (current.weather_code === 45 || current.weather_code === 48) {
                // Mgła - złe
                conditionsRating = 'Złe';
                riskFromWeather = 20;
            } else if (current.weather_code >= 51 && current.weather_code <= 57) {
                // Mżawka - umiarkowane
                conditionsRating = 'Umiarkowane';
                riskFromWeather = 10;
            } else if (current.weather_code >= 2 && current.weather_code <= 3) {
                // Zachmurzone - umiarkowane
                conditionsRating = 'Umiarkowane';
                riskFromWeather = 5;
            }
            
            // Analiza wiatru
            if (current.wind_speed_10m * 3.6 > 25) {
                if (conditionsRating === 'Dobre') conditionsRating = 'Złe';
                riskFromWeather += 30;
            } else if (current.wind_speed_10m * 3.6 > 20) {
                if (conditionsRating === 'Dobre') conditionsRating = 'Umiarkowane';
                riskFromWeather += 25;
            } else if (current.wind_speed_10m * 3.6 > 15) {
                if (conditionsRating === 'Dobre') conditionsRating = 'Umiarkowane';
                riskFromWeather += 15;
            }
            
            // Analiza zachmurzenia
            if (current.cloud_cover > 90) {
                if (conditionsRating === 'Dobre') conditionsRating = 'Umiarkowane';
                riskFromWeather += 10;
            }
            
            // Analiza widoczności
            if (current.visibility && current.visibility < 5000) {
                if (conditionsRating === 'Dobre') conditionsRating = 'Złe';
                riskFromWeather += 15;
            } else if (current.visibility && current.visibility < 10000) {
                if (conditionsRating === 'Dobre') conditionsRating = 'Umiarkowane';
                riskFromWeather += 5;
            }
            
            setWeatherData({
                temperature: Math.round(current.temperature_2m),
                apparentTemperature: Math.round(current.apparent_temperature),
                windSpeed: Math.round(current.wind_speed_10m * 3.6), // m/s na km/h
                windDirection: Math.round(current.wind_direction_10m),
                windDirectionName: windDirectionName,
                windGusts: Math.round((current.wind_gusts_10m || current.wind_speed_10m) * 3.6), // m/s na km/h
                humidity: Math.round(current.relative_humidity_2m),
                pressure: Math.round(current.pressure_msl || current.surface_pressure),
                cloudCover: Math.round(current.cloud_cover),
                visibility: current.visibility ? Math.round(current.visibility / 1000) : null, // m na km
                precipitation: current.precipitation || 0,
                rain: current.rain || 0,
                showers: current.showers || 0,
                snowfall: current.snowfall || 0,
                weatherCode: current.weather_code,
                weatherDescription: weatherDescription,
                conditionsRating: conditionsRating,
                riskFromWeather: riskFromWeather,
                isDay: current.is_day === 1,
                windImpact: windImpact,
                windImpactDescription: windImpactDescription,
                timestamp: new Date().toISOString()
            });
        } catch (error) {
            console.error('Błąd pobierania danych pogodowych:', error);
            // Fallback do podstawowych danych jeśli API nie działa
            setWeatherData({
                temperature: null,
                error: 'Nie udało się pobrać danych pogodowych. Sprawdź połączenie z internetem.'
            });
        } finally {
            setWeatherLoading(false);
        }
    };

    // Analiza ryzyka lotu (rozszerzona)
    const analyzeRisk = (flight) => {
        if (!flight.releaseDate || !flight.distance) {
            return null;
        }
        
        const risks = [];
        const riskDetails = [];
        let riskScore = 0;
        
        // Analiza dystansu
        if (flight.distance > 1000) {
            const points = 20;
            risks.push({ type: 'Długi dystans', severity: 'medium', message: 'Długi dystans zwiększa ryzyko strat', points });
            riskDetails.push({ factor: 'Dystans', value: `${flight.distance} km`, impact: 'Wysoki', points, reason: 'Dystans powyżej 1000 km znacznie zwiększa ryzyko strat i zmęczenia gołębi' });
            riskScore += points;
        } else if (flight.distance > 500) {
            const points = 10;
            risks.push({ type: 'Średni dystans', severity: 'low', message: 'Średni dystans - umiarkowane ryzyko', points });
            riskDetails.push({ factor: 'Dystans', value: `${flight.distance} km`, impact: 'Średni', points, reason: 'Dystans 500-1000 km wymaga dobrej kondycji gołębi' });
            riskScore += points;
        } else {
            riskDetails.push({ factor: 'Dystans', value: `${flight.distance} km`, impact: 'Niski', points: 0, reason: 'Krótki dystans - niskie ryzyko' });
        }
        
        // Analiza pory roku (sezon)
        const releaseDate = new Date(flight.releaseDate);
        const month = releaseDate.getMonth();
        const season = month >= 2 && month <= 4 ? 'Wiosna' : month >= 5 && month <= 7 ? 'Lato' : month >= 8 && month <= 10 ? 'Jesień' : 'Zima';
        
        if (month >= 10 || month <= 2) {
            const points = 30;
            risks.push({ type: 'Zimna pora roku', severity: 'high', message: 'Zimna pogoda może zwiększyć straty', points });
            riskDetails.push({ factor: 'Pora roku', value: season, impact: 'Wysoki', points, reason: 'Zimna pogoda zwiększa ryzyko hipotermii i strat' });
            riskScore += points;
        } else if (month >= 5 && month <= 7) {
            riskDetails.push({ factor: 'Pora roku', value: season, impact: 'Niski', points: 0, reason: 'Lato - optymalne warunki dla lotów' });
        } else {
            const points = 10;
            riskDetails.push({ factor: 'Pora roku', value: season, impact: 'Średni', points, reason: 'Pora przejściowa - umiarkowane warunki' });
            riskScore += points;
        }
        
        // Analiza liczby gołębi
        if (flight.pigeonsCount < 100) {
            const points = 10;
            risks.push({ type: 'Mała liczba gołębi', severity: 'low', message: 'Mniejsza konkurencja, ale większe ryzyko', points });
            riskDetails.push({ factor: 'Liczba gołębi', value: flight.pigeonsCount, impact: 'Niski', points, reason: 'Mała liczba gołębi może oznaczać mniejszą motywację do powrotu' });
            riskScore += points;
        } else if (flight.pigeonsCount > 1000) {
            riskDetails.push({ factor: 'Liczba gołębi', value: flight.pigeonsCount, impact: 'Pozytywny', points: -5, reason: 'Duża liczba gołębi zwiększa motywację i bezpieczeństwo' });
            riskScore = Math.max(0, riskScore - 5);
        } else {
            riskDetails.push({ factor: 'Liczba gołębi', value: flight.pigeonsCount, impact: 'Neutralny', points: 0, reason: 'Optymalna liczba gołębi' });
        }
        
        // Analiza kategorii
        if (flight.category === 'Maraton' || flight.category === 'M') {
            const points = 15;
            risks.push({ type: 'Trudna kategoria', severity: 'medium', message: 'Maratony wymagają większego doświadczenia', points });
            riskDetails.push({ factor: 'Kategoria', value: flight.category, impact: 'Wysoki', points, reason: 'Maratony to najtrudniejsze loty wymagające wyjątkowej kondycji' });
            riskScore += points;
        } else {
            riskDetails.push({ factor: 'Kategoria', value: flight.category || 'Nieokreślona', impact: 'Niski', points: 0, reason: 'Standardowa kategoria lotu' });
        }
        
        // Analiza warunków pogodowych (jeśli dostępne)
        if (weatherData && !weatherData.error) {
            // Analiza prędkości wiatru
            if (weatherData.windSpeed > 25) {
                const points = 30;
                risks.push({ type: 'Bardzo silny wiatr', severity: 'high', message: `Wiatr ${weatherData.windSpeed} km/h może znacznie utrudnić lot`, points });
                riskDetails.push({ factor: 'Prędkość wiatru', value: `${weatherData.windSpeed} km/h (${weatherData.windDirectionName})`, impact: 'Bardzo wysoki', points, reason: 'Bardzo silny wiatr znacznie utrudnia nawigację, zwiększa zużycie energii i ryzyko strat' });
                riskScore += points;
            } else if (weatherData.windSpeed > 20) {
                const points = 25;
                risks.push({ type: 'Silny wiatr', severity: 'high', message: `Wiatr ${weatherData.windSpeed} km/h może utrudnić lot`, points });
                riskDetails.push({ factor: 'Prędkość wiatru', value: `${weatherData.windSpeed} km/h (${weatherData.windDirectionName})`, impact: 'Wysoki', points, reason: 'Silny wiatr utrudnia nawigację i zwiększa zużycie energii' });
                riskScore += points;
            } else if (weatherData.windSpeed > 15) {
                const points = 15;
                riskDetails.push({ factor: 'Prędkość wiatru', value: `${weatherData.windSpeed} km/h (${weatherData.windDirectionName})`, impact: 'Średni', points, reason: 'Umiarkowany wiatr może wpłynąć na czas przelotu' });
                riskScore += points;
            } else if (weatherData.windSpeed < 5) {
                riskDetails.push({ factor: 'Prędkość wiatru', value: `${weatherData.windSpeed} km/h (${weatherData.windDirectionName})`, impact: 'Pozytywny', points: -5, reason: 'Słaby wiatr - optymalne warunki dla lotu' });
                riskScore = Math.max(0, riskScore - 5);
            } else {
                riskDetails.push({ factor: 'Prędkość wiatru', value: `${weatherData.windSpeed} km/h (${weatherData.windDirectionName})`, impact: 'Niski', points: 0, reason: 'Normalna prędkość wiatru' });
            }
            
            // Analiza porywów wiatru
            if (weatherData.windGusts && weatherData.windGusts > weatherData.windSpeed + 10) {
                const points = 10;
                risks.push({ type: 'Porywy wiatru', severity: 'medium', message: `Porywy do ${weatherData.windGusts} km/h mogą być niebezpieczne`, points });
                riskDetails.push({ factor: 'Porywy wiatru', value: `Do ${weatherData.windGusts} km/h`, impact: 'Średni', points, reason: 'Porywy wiatru mogą zaskoczyć gołębie i utrudnić nawigację' });
                riskScore += points;
            }
            
            // Analiza zachmurzenia
            if (weatherData.cloudCover > 80) {
                const points = 10;
                riskDetails.push({ factor: 'Zachmurzenie', value: `${weatherData.cloudCover}%`, impact: 'Średni', points, reason: 'Wysokie zachmurzenie może zmniejszyć widoczność' });
                riskScore += points;
            } else if (weatherData.cloudCover < 20) {
                riskDetails.push({ factor: 'Zachmurzenie', value: `${weatherData.cloudCover}%`, impact: 'Pozytywny', points: -3, reason: 'Niskie zachmurzenie - dobra widoczność' });
                riskScore = Math.max(0, riskScore - 3);
            } else {
                riskDetails.push({ factor: 'Zachmurzenie', value: `${weatherData.cloudCover}%`, impact: 'Niski', points: 0, reason: 'Normalne zachmurzenie' });
            }
            
            // Analiza warunków pogodowych (opady, mgła, burze)
            if (weatherData.weatherCode >= 61 && weatherData.weatherCode <= 67) {
                const points = 25;
                risks.push({ type: 'Opady deszczu', severity: 'high', message: `${weatherData.weatherDescription} może znacznie utrudnić lot`, points });
                riskDetails.push({ factor: 'Warunki pogodowe', value: weatherData.weatherDescription, impact: 'Wysoki', points, reason: 'Deszcz zmniejsza widoczność, utrudnia nawigację i zwiększa ryzyko hipotermii' });
                riskScore += points;
            } else if (weatherData.weatherCode >= 71 && weatherData.weatherCode <= 77) {
                const points = 30;
                risks.push({ type: 'Opady śniegu', severity: 'high', message: `${weatherData.weatherDescription} może być bardzo niebezpieczne`, points });
                riskDetails.push({ factor: 'Warunki pogodowe', value: weatherData.weatherDescription, impact: 'Bardzo wysoki', points, reason: 'Śnieg znacznie zmniejsza widoczność i może być śmiertelnie niebezpieczny dla gołębi' });
                riskScore += points;
            } else if (weatherData.weatherCode === 45 || weatherData.weatherCode === 48) {
                const points = 20;
                risks.push({ type: 'Mgła', severity: 'high', message: `${weatherData.weatherDescription} znacznie zmniejsza widoczność`, points });
                riskDetails.push({ factor: 'Warunki pogodowe', value: weatherData.weatherDescription, impact: 'Wysoki', points, reason: 'Mgła drastycznie zmniejsza widoczność i utrudnia nawigację' });
                riskScore += points;
            } else if (weatherData.weatherCode >= 95 && weatherData.weatherCode <= 99) {
                const points = 35;
                risks.push({ type: 'Burza', severity: 'high', message: `${weatherData.weatherDescription} - bardzo niebezpieczne warunki`, points });
                riskDetails.push({ factor: 'Warunki pogodowe', value: weatherData.weatherDescription, impact: 'Bardzo wysoki', points, reason: 'Burze są ekstremalnie niebezpieczne - absolutnie unikać lotów w takich warunkach' });
                riskScore += points;
            } else {
                riskDetails.push({ factor: 'Warunki pogodowe', value: weatherData.weatherDescription, impact: 'Niski', points: 0, reason: 'Dobre warunki pogodowe' });
            }
            
            // Analiza widoczności
            if (weatherData.visibility && weatherData.visibility < 5) {
                const points = 15;
                risks.push({ type: 'Słaba widoczność', severity: 'medium', message: `Widoczność ${weatherData.visibility} km może utrudnić nawigację`, points });
                riskDetails.push({ factor: 'Widoczność', value: `${weatherData.visibility} km`, impact: 'Średni', points, reason: 'Słaba widoczność utrudnia nawigację i zwiększa ryzyko błądzenia' });
                riskScore += points;
            } else if (weatherData.visibility && weatherData.visibility >= 10) {
                riskDetails.push({ factor: 'Widoczność', value: `${weatherData.visibility} km`, impact: 'Pozytywny', points: -2, reason: 'Dobra widoczność ułatwia nawigację' });
                riskScore = Math.max(0, riskScore - 2);
            } else if (weatherData.visibility) {
                riskDetails.push({ factor: 'Widoczność', value: `${weatherData.visibility} km`, impact: 'Niski', points: 0, reason: 'Normalna widoczność' });
            }
        }
        
        // Szacowanie szans na sukces
        const successChance = Math.max(0, 100 - riskScore);
        
        return {
            riskScore: Math.min(100, riskScore),
            successChance,
            risks,
            riskDetails,
            recommendation: riskScore > 50 ? 'Wysokie ryzyko - rozważ przesunięcie terminu' : 
                           riskScore > 30 ? 'Średnie ryzyko - monitoruj warunki pogodowe' : 
                           'Niskie ryzyko - optymalne warunki'
        };
    };

    // Zapisywanie lokalizacji gołębnika
    const saveHomeLocation = () => {
        const homeData = {
            lat: homeLocation.lat,
            lng: homeLocation.lng,
            name: homeLocationName
        };
        localStorage.setItem('homeLocation', JSON.stringify(homeData));
        setHomeLocationDialogOpen(false);
        alert('Lokalizacja gołębnika została zapisana jako domyślna!');
    };

    return (
        <div className={styles['content-container']}>
            <Grid container spacing={0} sx={{minHeight: '100vh', margin: 0}}>
                <Grid sx={{minHeight: '100vh', height: '100%', backgroundColor: '#478E94', position: { xs: 'relative', md: 'sticky' }, top: 0, bottom: 0, alignSelf: 'flex-start', margin: 0}} item xs={12} md={2}>
                    <Card sx={{
                        borderRadius: 0,
                        backgroundColor: '#478E94',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: { xs: 1, md: 2 },
                        boxShadow: 'none',
                    }}>
                        <CardMedia
                            component="img"
                            image={Logo}
                            alt="Logo"
                            sx={{
                                width: { xs: 120, md: '50%' },
                                maxWidth: '80%',
                                height: 'auto',
                                background: '#478E94',
                            }}
                        />
                    </Card>
                    
                    <div className={styles['all-icons-container']}>
                        {icons.map((icon, index) => (
                            <div
                                className={styles['icon-container']}
                                key={index}
                                tabIndex="0"
                                onClick={handleClick(index, iconLabels[index])}
                                style={{
                                    backgroundColor: index === 4 ? '#4c4c4c' : '#478E94'
                                }}
                            >
                                <img className={styles['custom-icon']} src={icon} alt={`Custom Icon ${index + 1}`} />
                                <p className={styles['icon-label']}>{iconLabels[index]}</p>
                            </div>
                        ))}
                    </div>
                </Grid>

                <Grid item xs={12} md={10}>
                    <div className={styles['header-container']}>
                        <div className={styles['text-header-container']}>
                            <span className={styles['text-grey-header-catalog']}>Loty</span>
                        </div>

                        <div className={styles['avatar-container']}>
                            <Avatar className={styles.avatar} alt="Name" src={AvatarImage} />
                            <Menu
                                id="profile-menu"
                                anchorEl={anchorEl}
                                keepMounted
                                open={Boolean(anchorEl)}
                                onClose={handleMenuClose}
                            >
                                <MenuItem onClick={handleMenuClose}>Profil</MenuItem>
                                <MenuItem onClick={handleMenuClose}>Ustawienia</MenuItem>
                                <MenuItem onClick={handleLogout}>Wyloguj</MenuItem>
                            </Menu>
                            <Typography sx={{fontSize: '18px', color: '#2A2A2A', fontFamily: "'Poppins', sans-serif", fontWeight: 500}}>Krzysztof</Typography>
                            <IconButton color="inherit" onClick={handleMenuOpen}>
                                <ExpandMoreIcon style={{fontSize: '3.6vh'}}/>
                            </IconButton>
                        </div>
                    </div>

                    {/* Statystyki */}
                    <Box sx={{ display: 'flex', gap: 2, margin: { xs: '16px', md: '20px 75px' }, marginBottom: 2, flexWrap: 'wrap' }}>
                        <Card sx={{ flex: 1, padding: 2, backgroundColor: '#f5f5f5' }}>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', color: '#707070' }}>
                                Liczba lotów
                            </Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '32px', fontWeight: 700, color: '#478E95' }}>
                                {stats.uniqueFlights}
                            </Typography>
                        </Card>
                        <Card sx={{ flex: 1, padding: 2, backgroundColor: '#f5f5f5' }}>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', color: '#707070' }}>
                                Top 1
                            </Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '32px', fontWeight: 700, color: '#57C09A' }}>
                                {stats.top1Count}
                            </Typography>
                        </Card>
                        <Card sx={{ flex: 1, padding: 2, backgroundColor: '#f5f5f5' }}>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', color: '#707070' }}>
                                Top 50
                            </Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '32px', fontWeight: 700, color: '#80D3B5' }}>
                                {stats.top50Count}
                            </Typography>
                        </Card>
                    </Box>

                    {/* Przyciski akcji */}
                    <Box sx={{ display: 'flex', gap: 2, margin: { xs: '0 16px', md: '0 75px' }, marginBottom: 2, flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddFlight}
                            sx={{
                                fontFamily: "'Poppins', sans-serif",
                                textTransform: 'none',
                                backgroundColor: '#57C09A',
                                padding: '10px 25px',
                                fontSize: '14px',
                                fontWeight: 600,
                                borderRadius: '20px',
                                '&:hover': {
                                    backgroundColor: '#4fcda2',
                                }
                            }}
                        >
                            Dodaj lot
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<UploadFileIcon />}
                            onClick={() => setImportDialogOpen(true)}
                            sx={{
                                fontFamily: "'Poppins', sans-serif",
                                textTransform: 'none',
                                backgroundColor: '#9C27B0',
                                padding: '10px 25px',
                                fontSize: '14px',
                                fontWeight: 600,
                                borderRadius: '20px',
                                '&:hover': {
                                    backgroundColor: '#7B1FA2',
                                }
                            }}
                        >
                            Importuj wyniki
                        </Button>
                        {flights.length > 0 && (
                            <>
                                <Button
                                    variant="outlined"
                                    startIcon={<SettingsIcon />}
                                    onClick={() => setHomeLocationDialogOpen(true)}
                                    sx={{
                                        fontFamily: "'Poppins', sans-serif",
                                        textTransform: 'none',
                                        borderColor: '#478E95',
                                        color: '#478E95',
                                        padding: '10px 25px',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        borderRadius: '20px',
                                        '&:hover': {
                                            borderColor: '#57C09A',
                                            backgroundColor: 'rgba(71, 142, 149, 0.1)',
                                        }
                                    }}
                                >
                                    Adres gołębnika
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<FlightTakeoffIcon />}
                                    onClick={() => {
                                        if (flights.length > 0) {
                                            setSelectedFlightForSim(flights[0]);
                                            setSimulatorOpen(true);
                                        }
                                    }}
                                    sx={{
                                        fontFamily: "'Poppins', sans-serif",
                                        textTransform: 'none',
                                        borderColor: '#478E95',
                                        color: '#478E95',
                                        padding: '10px 25px',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        borderRadius: '20px',
                                        '&:hover': {
                                            borderColor: '#57C09A',
                                            backgroundColor: 'rgba(71, 142, 149, 0.1)',
                                        }
                                    }}
                                >
                                    Symulator lotu
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<AnalyticsIcon />}
                                    onClick={() => {
                                        if (flights.length > 0) {
                                            setSelectedFlightForSim(flights[0]);
                                            setRiskAnalysisOpen(true);
                                        }
                                    }}
                                    sx={{
                                        fontFamily: "'Poppins', sans-serif",
                                        textTransform: 'none',
                                        borderColor: '#478E95',
                                        color: '#478E95',
                                        padding: '10px 25px',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        borderRadius: '20px',
                                        '&:hover': {
                                            borderColor: '#57C09A',
                                            backgroundColor: 'rgba(71, 142, 149, 0.1)',
                                        }
                                    }}
                                >
                                    Analiza ryzyka
                                </Button>
                            </>
                        )}
                    </Box>

                    {/* Filtrowanie */}
                    <Box sx={{ margin: { xs: '0 16px', md: '0 75px' }, marginBottom: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>
                            Filtruj po statusie:
                        </Typography>
                        <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                            <InputLabel sx={{ fontFamily: "'Poppins', sans-serif" }}>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                label="Status"
                                sx={{ fontFamily: "'Poppins', sans-serif" }}
                            >
                                <MenuItem value="all" sx={{ fontFamily: "'Poppins', sans-serif" }}>Wszystkie</MenuItem>
                                <MenuItem value="planned" sx={{ fontFamily: "'Poppins', sans-serif" }}>Zaplanowane</MenuItem>
                                <MenuItem value="completed" sx={{ fontFamily: "'Poppins', sans-serif" }}>Ukończone</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Lista lotów */}
                    <Box sx={{ margin: { xs: '0 16px', md: '0 75px' }, marginBottom: 4, overflowX: 'auto' }}>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>Nazwa lotu</TableCell>
                                        <TableCell sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>Data</TableCell>
                                        <TableCell sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>Miejsce wypuszczenia</TableCell>
                                        <TableCell sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>Dystans (km)</TableCell>
                                        <TableCell sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>Kategoria</TableCell>
                                        <TableCell sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>Status</TableCell>
                                        <TableCell sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>Pozycja</TableCell>
                                        <TableCell sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>Źródło</TableCell>
                                        <TableCell sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>Akcje</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(() => {
                                        const filteredFlights = statusFilter === 'all' 
                                            ? flights 
                                            : flights.filter(f => f.status === statusFilter);
                                        
                                        if (filteredFlights.length === 0) {
                                            return (
                                                <TableRow>
                                                    <TableCell colSpan={9} align="center" sx={{ fontFamily: "'Poppins', sans-serif", padding: 4 }}>
                                                        Brak lotów {statusFilter !== 'all' ? `ze statusem "${statusFilter === 'planned' ? 'Zaplanowane' : 'Ukończone'}"` : ''}. Dodaj pierwszy lot lub zaimportuj wyniki.
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        }
                                        
                                        return filteredFlights.map((flight) => (
                                            <TableRow key={flight.id}>
                                                <TableCell sx={{ fontFamily: "'Poppins', sans-serif" }}>{flight.name}</TableCell>
                                                <TableCell sx={{ fontFamily: "'Poppins', sans-serif" }}>{flight.releaseDate}</TableCell>
                                                <TableCell sx={{ fontFamily: "'Poppins', sans-serif" }}>{flight.releaseLocation}</TableCell>
                                                <TableCell sx={{ fontFamily: "'Poppins', sans-serif" }}>{flight.distance} km</TableCell>
                                                <TableCell sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                                    <Chip label={flight.category} size="small" sx={{ fontFamily: "'Poppins', sans-serif" }} />
                                                </TableCell>
                                                <TableCell sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                                    <Chip 
                                                        label={flight.status === 'completed' ? 'Ukończone' : 'Zaplanowane'} 
                                                        size="small" 
                                                        color={flight.status === 'completed' ? 'success' : 'warning'}
                                                        sx={{ fontFamily: "'Poppins', sans-serif" }} 
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                                    {flight.position ? `${flight.position} / ${flight.pigeonsCount}` : '-'}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                                    <Chip label={flight.source} size="small" variant="outlined" sx={{ fontFamily: "'Poppins', sans-serif" }} />
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title="Edytuj lot">
                                                        <IconButton size="small" onClick={() => handleEditFlight(flight)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Symulator lotu">
                                                        <IconButton size="small" onClick={() => {
                                                            setSelectedFlightForSim(flight);
                                                            setSimulatorOpen(true);
                                                        }}>
                                                            <FlightTakeoffIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Analiza ryzyka">
                                                        <IconButton size="small" onClick={() => {
                                                            setSelectedFlightForSim(flight);
                                                            setRiskAnalysisOpen(true);
                                                        }}>
                                                            <AnalyticsIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Usuń lot">
                                                        <IconButton size="small" onClick={() => handleDeleteFlight(flight.id)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ));
                                    })()}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Grid>
            </Grid>

            {/* Dialog dodawania/edycji lotu */}
            <Dialog open={addFlightOpen || editFlightOpen} onClose={editingFlight ? () => setEditFlightOpen(false) : handleCloseAddFlight} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '24px', color: '#478E95' }}>
                    {editingFlight ? 'Edytuj lot' : 'Dodaj nowy lot'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
                        <TextField
                            fullWidth
                            label="Nazwa lotu / konkursu *"
                            value={flightForm.name}
                            onChange={(e) => setFlightForm({...flightForm, name: e.target.value})}
                            sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                        />
                        <TextField
                            fullWidth
                            type="date"
                            label="Data wypuszczenia *"
                            value={flightForm.releaseDate}
                            onChange={(e) => setFlightForm({...flightForm, releaseDate: e.target.value})}
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                fullWidth
                                label="Miejsce wypuszczenia *"
                                value={flightForm.releaseLocation}
                                onChange={(e) => setFlightForm({...flightForm, releaseLocation: e.target.value})}
                                sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                            />
                            <Button
                                startIcon={<MapIcon />}
                                variant="outlined"
                                onClick={() => {
                                    if (flightForm.releaseLocation) {
                                        handleGeocode(flightForm.releaseLocation);
                                    } else {
                                        setMapDialogOpen(true);
                                    }
                                }}
                                sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none' }}
                            >
                                Mapa
                            </Button>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Szerokość geograficzna"
                                value={flightForm.releaseLat}
                                onChange={(e) => setFlightForm({...flightForm, releaseLat: e.target.value})}
                                sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                            />
                            <TextField
                                label="Długość geograficzna"
                                value={flightForm.releaseLng}
                                onChange={(e) => setFlightForm({...flightForm, releaseLng: e.target.value})}
                                sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Dystans (km) *"
                                value={flightForm.distance}
                                onChange={(e) => setFlightForm({...flightForm, distance: e.target.value})}
                                sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                            />
                            <FormControl fullWidth>
                                <InputLabel sx={{ fontFamily: "'Poppins', sans-serif" }}>Kategoria *</InputLabel>
                                <Select
                                    value={flightForm.category}
                                    onChange={(e) => setFlightForm({...flightForm, category: e.target.value})}
                                    label="Kategoria *"
                                    sx={{ fontFamily: "'Poppins', sans-serif" }}
                                >
                                    {categories.map(cat => (
                                        <MenuItem key={cat} value={cat} sx={{ fontFamily: "'Poppins', sans-serif" }}>{cat}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                type="number"
                                label="Liczba zgłoszonych gołębi *"
                                value={flightForm.pigeonsCount}
                                onChange={(e) => setFlightForm({...flightForm, pigeonsCount: e.target.value})}
                                sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                            />
                            <TextField
                                type="number"
                                label="Pozycja hodowcy / gołębia"
                                value={flightForm.position}
                                onChange={(e) => setFlightForm({...flightForm, position: e.target.value})}
                                sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                            />
                        </Box>
                        <TextField
                            fullWidth
                            label="Numer obrączki gołębia"
                            value={flightForm.ringNumber}
                            onChange={(e) => setFlightForm({...flightForm, ringNumber: e.target.value})}
                            sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                type="number"
                                label="Prędkość (m/min)"
                                value={flightForm.speed}
                                onChange={(e) => setFlightForm({...flightForm, speed: e.target.value})}
                                sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                            />
                            <TextField
                                type="number"
                                label="Miejsce w rankingu"
                                value={flightForm.rankingPosition}
                                onChange={(e) => setFlightForm({...flightForm, rankingPosition: e.target.value})}
                                sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                            />
                            <TextField
                                type="number"
                                label="Coeficient / punkty"
                                value={flightForm.coefficient}
                                onChange={(e) => setFlightForm({...flightForm, coefficient: e.target.value})}
                                sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel sx={{ fontFamily: "'Poppins', sans-serif" }}>Źródło danych</InputLabel>
                                <Select
                                    value={flightForm.source}
                                    onChange={(e) => setFlightForm({...flightForm, source: e.target.value})}
                                    label="Źródło danych"
                                    sx={{ fontFamily: "'Poppins', sans-serif" }}
                                >
                                    {sources.map(src => (
                                        <MenuItem key={src} value={src} sx={{ fontFamily: "'Poppins', sans-serif" }}>{src}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel sx={{ fontFamily: "'Poppins', sans-serif" }}>Status *</InputLabel>
                                <Select
                                    value={flightForm.status}
                                    onChange={(e) => setFlightForm({...flightForm, status: e.target.value})}
                                    label="Status *"
                                    sx={{ fontFamily: "'Poppins', sans-serif" }}
                                >
                                    <MenuItem value="planned" sx={{ fontFamily: "'Poppins', sans-serif" }}>Zaplanowane</MenuItem>
                                    <MenuItem value="completed" sx={{ fontFamily: "'Poppins', sans-serif" }}>Ukończone</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Notatki"
                            value={flightForm.notes}
                            onChange={(e) => setFlightForm({...flightForm, notes: e.target.value})}
                            sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ padding: '20px' }}>
                    <Button
                        onClick={editingFlight ? () => setEditFlightOpen(false) : handleCloseAddFlight}
                        sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none', color: '#707070' }}
                    >
                        Anuluj
                    </Button>
                    <Button
                        onClick={handleSaveFlight}
                        variant="contained"
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            textTransform: 'none',
                            backgroundColor: '#57C09A',
                            '&:hover': { backgroundColor: '#4fcda2' }
                        }}
                    >
                        Zapisz
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog importu */}
            <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '24px', color: '#478E95' }}>
                    Importuj wyniki lotów
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 2 }}>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif" }}>
                            Wybierz format pliku do importu:
                        </Typography>
                        <FormControl fullWidth>
                            <InputLabel sx={{ fontFamily: "'Poppins', sans-serif" }}>Format pliku</InputLabel>
                            <Select
                                defaultValue="pdf"
                                label="Format pliku"
                                sx={{ fontFamily: "'Poppins', sans-serif" }}
                            >
                                <MenuItem value="pdf" sx={{ fontFamily: "'Poppins', sans-serif" }}>PDF (Lista konkursowa)</MenuItem>
                                <MenuItem value="csv" sx={{ fontFamily: "'Poppins', sans-serif" }}>CSV</MenuItem>
                                <MenuItem value="excel" sx={{ fontFamily: "'Poppins', sans-serif" }}>Excel</MenuItem>
                                <MenuItem value="txt" sx={{ fontFamily: "'Poppins', sans-serif" }}>TXT</MenuItem>
                            </Select>
                        </FormControl>
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<CloudUploadIcon />}
                            sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none' }}
                        >
                            Wybierz plik
                            <input type="file" hidden accept=".pdf,.csv,.xlsx,.xls,.txt" />
                        </Button>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#707070' }}>
                            Obsługiwane formaty: PDF, CSV, Excel, TXT
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ padding: '20px' }}>
                    <Button
                        onClick={() => setImportDialogOpen(false)}
                        sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none', color: '#707070' }}
                    >
                        Anuluj
                    </Button>
                    <Button
                        variant="contained"
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            textTransform: 'none',
                            backgroundColor: '#57C09A',
                            '&:hover': { backgroundColor: '#4fcda2' }
                        }}
                    >
                        Importuj
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog mapy */}
            <Dialog open={mapDialogOpen} onClose={() => setMapDialogOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '24px', color: '#478E95' }}>
                    Wybierz miejsce wypuszczenia
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ marginTop: 2, marginBottom: 2 }}>
                        <TextField
                            fullWidth
                            label="Wyszukaj miejsce"
                            value={flightForm.releaseLocation}
                            onChange={(e) => {
                                setFlightForm({...flightForm, releaseLocation: e.target.value});
                            }}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && flightForm.releaseLocation) {
                                    handleGeocode(flightForm.releaseLocation);
                                }
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                        />
                        <Button
                            onClick={() => handleGeocode(flightForm.releaseLocation)}
                            sx={{ marginTop: 1, fontFamily: "'Poppins', sans-serif", textTransform: 'none' }}
                        >
                            Wyszukaj
                        </Button>
                    </Box>
                    <Box sx={{ height: '500px', marginTop: 2, position: 'relative', border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                        <MapContainer
                            center={[mapCenter.lat, mapCenter.lng]}
                            zoom={selectedLocation ? 8 : 6}
                            style={{ height: '100%', width: '100%' }}
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <MapClickHandler onClick={handleMapClick} />
                            {selectedLocation && (
                                <Marker position={[selectedLocation.lat, selectedLocation.lng]}>
                                    <Popup>Miejsce wypuszczenia</Popup>
                                </Marker>
                            )}
                            {homeLocation && (
                                <Marker 
                                    position={[homeLocation.lat, homeLocation.lng]}
                                    icon={L.icon({
                                        iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
                                        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
                                        iconSize: [25, 41],
                                        iconAnchor: [12, 41],
                                        popupAnchor: [1, -34],
                                        shadowSize: [41, 41]
                                    })}
                                >
                                    <Popup>Gołębnik</Popup>
                                </Marker>
                            )}
                            {selectedLocation && homeLocation && (
                                <Polyline
                                    positions={[
                                        [selectedLocation.lat, selectedLocation.lng],
                                        [homeLocation.lat, homeLocation.lng]
                                    ]}
                                    color="#478E95"
                                    weight={3}
                                    opacity={0.7}
                                />
                            )}
                        </MapContainer>
                    </Box>
                    {selectedLocation && (
                        <Box sx={{ marginTop: 2 }}>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                Współrzędne: {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                            </Typography>
                            {homeLocation.lat && homeLocation.lng && (
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                    Dystans: {calculateDistance(homeLocation.lat, homeLocation.lng, selectedLocation.lat, selectedLocation.lng).toFixed(2)} km
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ padding: '20px' }}>
                    <Button
                        onClick={() => setMapDialogOpen(false)}
                        sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none', color: '#707070' }}
                    >
                        Anuluj
                    </Button>
                    <Button
                        onClick={() => {
                            if (selectedLocation) {
                                setFlightForm({
                                    ...flightForm,
                                    releaseLat: selectedLocation.lat.toString(),
                                    releaseLng: selectedLocation.lng.toString()
                                });
                            }
                            setMapDialogOpen(false);
                        }}
                        variant="contained"
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            textTransform: 'none',
                            backgroundColor: '#57C09A',
                            '&:hover': { backgroundColor: '#4fcda2' }
                        }}
                    >
                        Zatwierdź
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog symulatora lotu */}
            <Dialog open={simulatorOpen} onClose={() => setSimulatorOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '24px', color: '#478E95' }}>
                    Symulator lotu - {selectedFlightForSim?.name}
                </DialogTitle>
                <DialogContent>
                    {selectedFlightForSim && (() => {
                        const simulation = simulateFlight(selectedFlightForSim);
                        if (!simulation) {
                            return (
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", padding: 2 }}>
                                    Brak danych do symulacji. Uzupełnij współrzędne i dystans.
                                </Typography>
                            );
                        }
                        return (
                            <Box sx={{ marginTop: 2 }}>
                                {(() => {
                                    const progressRatio = simulatorProgress / 100;
                                    const start = simulation.route[0];
                                    const end = simulation.route[1];
                                    const currentLat = start.lat + (end.lat - start.lat) * progressRatio;
                                    const currentLng = start.lng + (end.lng - start.lng) * progressRatio;
                                    const distanceCovered = simulation.distance * progressRatio;
                                    const remainingDistance = Math.max(0, simulation.distance - distanceCovered);
                                    const weatherSnapshot = getAnimatedWeatherSnapshot(weatherData, simulatorProgress);
                                    const speedWeatherFactor = weatherSnapshot
                                        ? clamp(1 - Math.max(0, weatherSnapshot.windSpeed - 12) * 0.01, 0.72, 1.08)
                                        : 1;
                                    const selectedPigeon = pigeonsList.find((p) => (p.ID || p.id) === selectedPigeonId);
                                    const releaseTimeMs = simReleaseDateTime ? new Date(simReleaseDateTime).getTime() : null;
                                    const arrivalTimeMs = simArrivalDateTime ? new Date(simArrivalDateTime).getTime() : null;
                                    const flightDurationMinutes = releaseTimeMs && arrivalTimeMs && arrivalTimeMs > releaseTimeMs
                                        ? (arrivalTimeMs - releaseTimeMs) / (1000 * 60)
                                        : null;
                                    const isFlightTimeValid = Boolean(flightDurationMinutes);
                                    const effectiveFlightDurationMinutes = flightDurationMinutes || simulation.timeMinutes;
                                    const elapsedMinutes = effectiveFlightDurationMinutes * progressRatio;
                                    const elapsedHours = Math.floor(elapsedMinutes / 60);
                                    const elapsedMins = Math.floor(elapsedMinutes % 60);
                                    const calculatedAvgSpeed = flightDurationMinutes
                                        ? Math.round((simulation.distance * 1000) / flightDurationMinutes)
                                        : null;
                                    const baseSpeed = calculatedAvgSpeed || simulation.estimatedSpeed;
                                    const currentSpeed = Math.round(baseSpeed * speedWeatherFactor);
                                    const effectiveSpeed = calculatedAvgSpeed || currentSpeed;
                                    const effectiveEtaMinutes = effectiveSpeed > 0 ? (remainingDistance * 1000) / effectiveSpeed : 0;
                                    const etaHours = Math.floor(effectiveEtaMinutes / 60);
                                    const etaMins = Math.floor(effectiveEtaMinutes % 60);
                                    const windField = getWindFieldPoints(start, end, weatherSnapshot);

                                    return (
                                        <>
                                <Box sx={{ display: 'flex', gap: 2, marginBottom: 3 }}>
                                    <Card sx={{ flex: 1, padding: 2, backgroundColor: '#f5f5f5' }}>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', color: '#707070' }}>
                                            Czas lotu
                                        </Typography>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '28px', fontWeight: 700, color: '#478E95' }}>
                                            {formatDuration(effectiveFlightDurationMinutes)}
                                        </Typography>
                                    </Card>
                                    <Card sx={{ flex: 1, padding: 2, backgroundColor: '#f5f5f5' }}>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', color: '#707070' }}>
                                            Aktualna prędkość
                                        </Typography>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '28px', fontWeight: 700, color: '#57C09A' }}>
                                            {currentSpeed} m/min
                                        </Typography>
                                    </Card>
                                    <Card sx={{ flex: 1, padding: 2, backgroundColor: '#f5f5f5' }}>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', color: '#707070' }}>
                                            Postęp lotu
                                        </Typography>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '28px', fontWeight: 700, color: '#80D3B5' }}>
                                            {simulatorProgress.toFixed(0)}%
                                        </Typography>
                                    </Card>
                                </Box>
                                <Box sx={{ marginBottom: 2, padding: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', marginBottom: 1, flexWrap: 'wrap' }}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={simulatorPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                                            disabled={!isFlightTimeValid}
                                            onClick={() => {
                                                if (!isFlightTimeValid) return;
                                                setSimulatorPlaying((prev) => !prev);
                                            }}
                                            sx={{ textTransform: 'none', backgroundColor: '#478E95', fontFamily: "'Poppins', sans-serif" }}
                                        >
                                            {simulatorPlaying ? 'Pauza' : 'Start'}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<StopIcon />}
                                            onClick={() => {
                                                setSimulatorPlaying(false);
                                                setSimulatorProgress(0);
                                            }}
                                            sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif" }}
                                        >
                                            Reset
                                        </Button>
                                        <Button size="small" onClick={() => setSimulatorSpeed(1)} sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif" }}>x1</Button>
                                        <Button size="small" onClick={() => setSimulatorSpeed(2)} sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif" }}>x2</Button>
                                        <Button size="small" onClick={() => setSimulatorSpeed(5)} sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif" }}>x5</Button>
                                        <Button size="small" onClick={() => setSimulatorSpeed(10)} sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif" }}>x10</Button>
                                    </Box>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5, marginBottom: 1.5 }}>
                                        <FormControl size="small" fullWidth>
                                            <InputLabel sx={{ fontFamily: "'Poppins', sans-serif" }}>Gołąb z hodowli</InputLabel>
                                            <Select
                                                value={selectedPigeonId}
                                                label="Gołąb z hodowli"
                                                onChange={(e) => setSelectedPigeonId(e.target.value)}
                                                sx={{ fontFamily: "'Poppins', sans-serif" }}
                                            >
                                                {pigeonsList.map((pigeon) => {
                                                    const id = pigeon.ID || pigeon.id;
                                                    const label = pigeon.name || pigeon.ringNumber || pigeon.ID || 'Gołąb';
                                                    return (
                                                        <MenuItem key={id} value={id} sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                                            {label}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            type="datetime-local"
                                            label="Start lotu"
                                            size="small"
                                            value={simReleaseDateTime}
                                            onChange={(e) => setSimReleaseDateTime(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                                        />
                                        <TextField
                                            type="datetime-local"
                                            label="Przylot do gołębnika"
                                            size="small"
                                            value={simArrivalDateTime}
                                            onChange={(e) => setSimArrivalDateTime(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                                        />
                                    </Box>
                                    {!isFlightTimeValid && (
                                        <Typography sx={{ color: '#d32f2f', fontFamily: "'Poppins', sans-serif", fontSize: 13, marginBottom: 1 }}>
                                            Uzupelnij poprawnie czas lotu: "Przylot do golebnika" musi byc pozniejszy niz "Start lotu".
                                        </Typography>
                                    )}
                                    <Slider
                                        min={0}
                                        max={100}
                                        step={1}
                                        value={simulatorProgress}
                                        onChange={(_, value) => setSimulatorProgress(value)}
                                        sx={{ color: '#478E95' }}
                                    />
                                    <LinearProgress variant="determinate" value={simulatorProgress} sx={{ height: 8, borderRadius: 5, marginTop: 1 }} />
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, marginTop: 2 }}>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: 13 }}><strong>Przebyto:</strong> {distanceCovered.toFixed(1)} km</Typography>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: 13 }}><strong>Pozostało:</strong> {remainingDistance.toFixed(1)} km</Typography>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: 13 }}><strong>Czas lotu:</strong> {elapsedHours}h {elapsedMins}min</Typography>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: 13 }}><strong>ETA:</strong> {etaHours}h {etaMins}min</Typography>
                                    </Box>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: 13, marginTop: 1 }}>
                                        <strong>Gołąb:</strong> {selectedPigeon ? (selectedPigeon.name || selectedPigeon.ringNumber || selectedPigeon.ID) : '—'} | <strong>Średnia prędkość:</strong> {(calculatedAvgSpeed || simulation.estimatedSpeed)} m/min | <strong>Czas realny:</strong> {flightDurationMinutes ? formatDuration(flightDurationMinutes) : 'uzupełnij start i przylot'}
                                    </Typography>
                                </Box>
                                {weatherSnapshot && (
                                    <Box sx={{ marginBottom: 2, padding: 2, border: '1px solid #bbdefb', backgroundColor: '#f5fbff', borderRadius: 2 }}>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, marginBottom: 1 }}>
                                            Warunki pogodowe (animowane)
                                        </Typography>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 1.5 }}>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: 13 }}>Wiatr: <strong>{weatherSnapshot.windSpeed} km/h</strong></Typography>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: 13 }}>Porywy: <strong>{weatherSnapshot.windGusts} km/h</strong></Typography>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: 13 }}>Temp.: <strong>{weatherSnapshot.temperature}°C</strong></Typography>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: 13 }}>Zachm.: <strong>{weatherSnapshot.cloudCover}%</strong></Typography>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: 13 }}>Wilg.: <strong>{weatherSnapshot.humidity}%</strong></Typography>
                                        </Box>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 1, marginBottom: 1, gap: 2, flexWrap: 'wrap' }}>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>
                                        Mapa trasy + wiatr + temperatura (jedna mapa)
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<SettingsIcon />}
                                        onClick={() => setHomeLocationDialogOpen(true)}
                                        sx={{ textTransform: 'none', fontFamily: "'Poppins', sans-serif" }}
                                    >
                                        Zmień adres gołębnika
                                    </Button>
                                </Box>
                                <Box sx={{ height: '430px', marginTop: 1, position: 'relative', border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                                        <MapContainer
                                            center={[
                                                (simulation.route[0].lat + simulation.route[1].lat) / 2,
                                                (simulation.route[0].lng + simulation.route[1].lng) / 2
                                            ]}
                                            zoom={7}
                                            style={{ height: '100%', width: '100%' }}
                                        >
                                            <TileLayer
                                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            />
                                            {windField.map((windPoint) => (
                                                <Marker
                                                    key={windPoint.key}
                                                    position={[windPoint.lat, windPoint.lng]}
                                                    icon={L.divIcon({
                                                        className: 'wind-arrow-marker',
                                                        html: `<div style="transform: rotate(${windPoint.angle}deg); font-size: 18px; color: ${windPoint.color}; text-shadow: 0 1px 2px rgba(0,0,0,0.35);">➤</div>`,
                                                        iconSize: [18, 18],
                                                        iconAnchor: [9, 9]
                                                    })}
                                                >
                                                    <Popup>Wiatr: {windPoint.speed} km/h</Popup>
                                                </Marker>
                                            ))}
                                            <Marker 
                                                position={[simulation.route[0].lat, simulation.route[0].lng]}
                                                icon={L.icon({
                                                    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
                                                    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
                                                    iconSize: [25, 41],
                                                    iconAnchor: [12, 41],
                                                    popupAnchor: [1, -34],
                                                    shadowSize: [41, 41]
                                                })}
                                            >
                                                <Popup>Miejsce wypuszczenia</Popup>
                                            </Marker>
                                            <Marker 
                                                position={[simulation.route[1].lat, simulation.route[1].lng]}
                                                icon={L.icon({
                                                    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
                                                    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
                                                    iconSize: [25, 41],
                                                    iconAnchor: [12, 41],
                                                    popupAnchor: [1, -34],
                                                    shadowSize: [41, 41]
                                                })}
                                            >
                                                <Popup>{homeLocationName || 'Gołębnik'}</Popup>
                                            </Marker>
                                            <Polyline
                                                positions={[
                                                    [simulation.route[0].lat, simulation.route[0].lng],
                                                    [simulation.route[1].lat, simulation.route[1].lng]
                                                ]}
                                                color="#478E95"
                                                weight={4}
                                                opacity={0.8}
                                            />
                                            <Polyline
                                                positions={[
                                                    [simulation.route[0].lat, simulation.route[0].lng],
                                                    [currentLat, currentLng]
                                                ]}
                                                color="#57C09A"
                                                weight={6}
                                                opacity={0.95}
                                            />
                                            <Marker
                                                position={[currentLat, currentLng]}
                                                icon={pigeon3DIcon}
                                            >
                                                <Popup>
                                                    Lot w toku: {simulatorProgress.toFixed(0)}% | Temp: {weatherSnapshot?.temperature ?? '—'}°C
                                                </Popup>
                                            </Marker>
                                        </MapContainer>
                                </Box>
                                        </>
                                    );
                                })()}
                            </Box>
                        );
                    })()}
                </DialogContent>
                <DialogActions sx={{ padding: '20px' }}>
                    <Button
                        onClick={() => {
                            setSimulatorOpen(false);
                            setSimulatorPlaying(false);
                        }}
                        variant="contained"
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            textTransform: 'none',
                            backgroundColor: '#57C09A',
                            '&:hover': { backgroundColor: '#4fcda2' }
                        }}
                    >
                        Zamknij
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog analizy ryzyka */}
            <Dialog open={riskAnalysisOpen} onClose={() => {
                setRiskAnalysisOpen(false);
                setWeatherData(null);
            }} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '24px', color: '#478E95' }}>
                    Analiza ryzyka - {selectedFlightForSim?.name}
                </DialogTitle>
                <DialogContent>
                    {selectedFlightForSim && (() => {
                        const analysis = analyzeRisk(selectedFlightForSim);
                        if (!analysis) {
                            return (
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", padding: 2 }}>
                                    Brak danych do analizy. Uzupełnij datę i dystans.
                                </Typography>
                            );
                        }
                        return (
                            <Box sx={{ marginTop: 2 }}>
                                <Box sx={{ display: 'flex', gap: 2, marginBottom: 3 }}>
                                    <Card sx={{ flex: 1, padding: 2, backgroundColor: analysis.riskScore > 50 ? '#ffebee' : analysis.riskScore > 30 ? '#fff3e0' : '#e8f5e9' }}>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', color: '#707070' }}>
                                            Poziom ryzyka
                                        </Typography>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '32px', fontWeight: 700, color: analysis.riskScore > 50 ? '#d32f2f' : analysis.riskScore > 30 ? '#f57c00' : '#388e3c' }}>
                                            {analysis.riskScore}%
                                        </Typography>
                                    </Card>
                                    <Card sx={{ flex: 1, padding: 2, backgroundColor: '#f5f5f5' }}>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', color: '#707070' }}>
                                            Szansa na sukces
                                        </Typography>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '32px', fontWeight: 700, color: '#57C09A' }}>
                                            {analysis.successChance}%
                                        </Typography>
                                    </Card>
                                </Box>

                                {/* Warunki pogodowe */}
                                {weatherLoading ? (
                                    <Box sx={{ marginBottom: 2, padding: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif" }}>Ładowanie danych pogodowych...</Typography>
                                    </Box>
                                ) : weatherData && weatherData.error ? (
                                    <Box sx={{ marginBottom: 2, padding: 2, backgroundColor: '#ffebee', borderRadius: 2, border: '1px solid #ef5350' }}>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", color: '#d32f2f' }}>
                                            {weatherData.error}
                                        </Typography>
                                    </Box>
                                ) : weatherData && (
                                    <Box sx={{ marginBottom: 2, padding: 2, backgroundColor: '#e3f2fd', borderRadius: 2, border: '1px solid #90caf9' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '18px' }}>
                                                Warunki pogodowe
                                            </Typography>
                                            <Chip 
                                                label={weatherData.conditionsRating} 
                                                color={
                                                    weatherData.conditionsRating === 'Dobre' ? 'success' : 
                                                    weatherData.conditionsRating === 'Umiarkowane' ? 'warning' : 
                                                    weatherData.conditionsRating === 'Bardzo złe' ? 'error' : 'error'
                                                }
                                                sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}
                                            />
                                        </Box>
                                        
                                        {/* Główne parametry */}
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, marginBottom: 2 }}>
                                            <Card sx={{ padding: 1.5, backgroundColor: '#ffffff', textAlign: 'center' }}>
                                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#707070' }}>
                                                    Temperatura
                                                </Typography>
                                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '24px', fontWeight: 700, color: '#478E95' }}>
                                                    {weatherData.temperature}°C
                                                </Typography>
                                                {weatherData.apparentTemperature && (
                                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '10px', color: '#707070' }}>
                                                        Odczuwalna: {weatherData.apparentTemperature}°C
                                                    </Typography>
                                                )}
                                            </Card>
                                            <Card sx={{ padding: 1.5, backgroundColor: '#ffffff', textAlign: 'center' }}>
                                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#707070' }}>
                                                    Wiatr
                                                </Typography>
                                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '24px', fontWeight: 700, color: '#478E95' }}>
                                                    {weatherData.windSpeed} km/h
                                                </Typography>
                                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '10px', color: '#707070' }}>
                                                    {weatherData.windDirectionName} ({weatherData.windDirection}°)
                                                </Typography>
                                            </Card>
                                            <Card sx={{ padding: 1.5, backgroundColor: '#ffffff', textAlign: 'center' }}>
                                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#707070' }}>
                                                    Zachmurzenie
                                                </Typography>
                                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '24px', fontWeight: 700, color: '#478E95' }}>
                                                    {weatherData.cloudCover}%
                                                </Typography>
                                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '10px', color: '#707070' }}>
                                                    {weatherData.weatherDescription}
                                                </Typography>
                                            </Card>
                                        </Box>

                                        {/* Szczegółowe parametry */}
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
                                            <Box sx={{ padding: 1, backgroundColor: '#ffffff', borderRadius: 1 }}>
                                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#707070' }}>
                                                    Prędkość wiatru
                                                </Typography>
                                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '16px', fontWeight: 600 }}>
                                                    {weatherData.windSpeed} km/h
                                                </Typography>
                                                {weatherData.windGusts && weatherData.windGusts > weatherData.windSpeed && (
                                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '10px', color: '#f57c00' }}>
                                                        Porywy do {weatherData.windGusts} km/h
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Box sx={{ padding: 1, backgroundColor: '#ffffff', borderRadius: 1 }}>
                                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#707070' }}>
                                                    Kierunek wiatru
                                                </Typography>
                                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '16px', fontWeight: 600 }}>
                                                    {weatherData.windDirectionName} ({weatherData.windDirection}°)
                                                </Typography>
                                            </Box>
                                            <Box sx={{ padding: 1, backgroundColor: '#ffffff', borderRadius: 1 }}>
                                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#707070' }}>
                                                    Wilgotność
                                                </Typography>
                                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '16px', fontWeight: 600 }}>
                                                    {weatherData.humidity}%
                                                </Typography>
                                            </Box>
                                            <Box sx={{ padding: 1, backgroundColor: '#ffffff', borderRadius: 1 }}>
                                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#707070' }}>
                                                    Ciśnienie
                                                </Typography>
                                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '16px', fontWeight: 600 }}>
                                                    {weatherData.pressure} hPa
                                                </Typography>
                                            </Box>
                                            {weatherData.visibility && (
                                                <Box sx={{ padding: 1, backgroundColor: '#ffffff', borderRadius: 1 }}>
                                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#707070' }}>
                                                        Widoczność
                                                    </Typography>
                                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '16px', fontWeight: 600 }}>
                                                        {weatherData.visibility} km
                                                    </Typography>
                                                </Box>
                                            )}
                                            {(weatherData.precipitation > 0 || weatherData.rain > 0 || weatherData.showers > 0 || weatherData.snowfall > 0) && (
                                                <Box sx={{ padding: 1, backgroundColor: '#ffffff', borderRadius: 1 }}>
                                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#707070' }}>
                                                        Opady
                                                    </Typography>
                                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '16px', fontWeight: 600 }}>
                                                        {weatherData.precipitation > 0 && `${weatherData.precipitation.toFixed(1)} mm`}
                                                        {weatherData.rain > 0 && ` Deszcz: ${weatherData.rain.toFixed(1)} mm`}
                                                        {weatherData.snowfall > 0 && ` Śnieg: ${weatherData.snowfall.toFixed(1)} cm`}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>

                                        {/* Wpływ wiatru */}
                                        <Box sx={{ marginTop: 2, padding: 1.5, backgroundColor: weatherData.windImpact === 'negatywny' ? '#ffebee' : weatherData.windImpact === 'pozytywny' ? '#e8f5e9' : '#f5f5f5', borderRadius: 1, border: `1px solid ${weatherData.windImpact === 'negatywny' ? '#ef5350' : weatherData.windImpact === 'pozytywny' ? '#66bb6a' : '#bdbdbd'}` }}>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '14px', marginBottom: 0.5 }}>
                                                Wpływ wiatru na lot:
                                            </Typography>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '13px', color: '#707070' }}>
                                                {weatherData.windImpact === 'negatywny' ? '⚠️ ' : weatherData.windImpact === 'pozytywny' ? '✅ ' : '➖ '}
                                                {weatherData.windImpactDescription}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                
                                <Box sx={{ marginBottom: 2 }}>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '18px', marginBottom: 1 }}>
                                        Rekomendacja:
                                    </Typography>
                                    <Box sx={{ 
                                        padding: 2, 
                                        borderRadius: 2, 
                                        backgroundColor: analysis.riskScore > 50 ? '#ffebee' : analysis.riskScore > 30 ? '#fff3e0' : '#e8f5e9',
                                        border: `2px solid ${analysis.riskScore > 50 ? '#d32f2f' : analysis.riskScore > 30 ? '#f57c00' : '#388e3c'}`
                                    }}>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '16px' }}>
                                            {analysis.recommendation}
                                        </Typography>
                                    </Box>
                                </Box>

                                {weatherData && !weatherData.error && selectedFlightForSim?.releaseLat && selectedFlightForSim?.releaseLng && (
                                    <Box sx={{ marginBottom: 2 }}>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '18px', marginBottom: 1 }}>
                                            Mapa trasy i warunków (wiatr + temperatura)
                                        </Typography>
                                        <Box sx={{ height: 320, border: '1px solid #dfe6eb', borderRadius: 2, overflow: 'hidden' }}>
                                            {(() => {
                                                const start = { lat: parseFloat(selectedFlightForSim.releaseLat), lng: parseFloat(selectedFlightForSim.releaseLng) };
                                                const end = homeLocation;
                                                const riskWind = getWindFieldPoints(start, end, weatherData);
                                                return (
                                                    <MapContainer
                                                        center={[(start.lat + end.lat) / 2, (start.lng + end.lng) / 2]}
                                                        zoom={7}
                                                        style={{ height: '100%', width: '100%' }}
                                                    >
                                                        <TileLayer
                                                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                        />
                                                        {riskWind.map((windPoint) => (
                                                            <Marker
                                                                key={`risk-${windPoint.key}`}
                                                                position={[windPoint.lat, windPoint.lng]}
                                                                icon={L.divIcon({
                                                                    className: 'wind-arrow-marker',
                                                                    html: `<div style="transform: rotate(${windPoint.angle}deg); font-size: 16px; color: ${windPoint.color}; text-shadow: 0 1px 2px rgba(0,0,0,0.35);">➤</div>`,
                                                                    iconSize: [16, 16],
                                                                    iconAnchor: [8, 8]
                                                                })}
                                                            />
                                                        ))}
                                                        <Marker position={[start.lat, start.lng]}>
                                                            <Popup>Miejsce wypuszczenia</Popup>
                                                        </Marker>
                                                        <Marker
                                                            position={[end.lat, end.lng]}
                                                            icon={L.icon({
                                                                iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
                                                                shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
                                                                iconSize: [25, 41],
                                                                iconAnchor: [12, 41],
                                                                popupAnchor: [1, -34],
                                                                shadowSize: [41, 41]
                                                            })}
                                                        >
                                                            <Popup>{homeLocationName || 'Gołębnik'} | {weatherData.temperature}°C</Popup>
                                                        </Marker>
                                                        <Polyline
                                                            positions={[[start.lat, start.lng], [end.lat, end.lng]]}
                                                            color="#478E95"
                                                            weight={4}
                                                            opacity={0.9}
                                                        />
                                                    </MapContainer>
                                                );
                                            })()}
                                        </Box>
                                    </Box>
                                )}

                                {/* Szczegóły obliczeń */}
                                {analysis.riskDetails && analysis.riskDetails.length > 0 && (
                                    <Box sx={{ marginBottom: 2 }}>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '18px', marginBottom: 1 }}>
                                            Szczegóły obliczeń ryzyka:
                                        </Typography>
                                        <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>Czynnik</TableCell>
                                                        <TableCell sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>Wartość</TableCell>
                                                        <TableCell sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>Wpływ</TableCell>
                                                        <TableCell sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>Punkty</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {analysis.riskDetails.map((detail, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell sx={{ fontFamily: "'Poppins', sans-serif" }}>{detail.factor}</TableCell>
                                                            <TableCell sx={{ fontFamily: "'Poppins', sans-serif" }}>{detail.value}</TableCell>
                                                            <TableCell sx={{ fontFamily: "'Poppins', sans-serif" }}>
                                                                <Chip 
                                                                    label={detail.impact} 
                                                                    size="small" 
                                                                    color={detail.impact === 'Wysoki' ? 'error' : detail.impact === 'Średni' ? 'warning' : 'success'}
                                                                    sx={{ fontFamily: "'Poppins', sans-serif" }}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>
                                                                {detail.points > 0 ? `+${detail.points}` : detail.points}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#707070', marginTop: 1, fontStyle: 'italic' }}>
                                            Suma punktów: <strong>{analysis.riskScore}</strong> (maksymalnie 100)
                                        </Typography>
                                    </Box>
                                )}

                                {analysis.risks.length > 0 && (
                                    <Box>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '18px', marginBottom: 1 }}>
                                            Zidentyfikowane ryzyka:
                                        </Typography>
                                        {analysis.risks.map((risk, index) => (
                                            <Box key={index} sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: 1, 
                                                padding: 1.5, 
                                                marginBottom: 1,
                                                borderRadius: 1,
                                                backgroundColor: risk.severity === 'high' ? '#ffebee' : risk.severity === 'medium' ? '#fff3e0' : '#f5f5f5'
                                            }}>
                                                <WarningIcon sx={{ 
                                                    color: risk.severity === 'high' ? '#d32f2f' : risk.severity === 'medium' ? '#f57c00' : '#757575' 
                                                }} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>
                                                        {risk.type} {risk.points && `(+${risk.points} pkt)`}
                                                    </Typography>
                                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '14px', color: '#707070' }}>
                                                        {risk.message}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        );
                    })()}
                </DialogContent>
                <DialogActions sx={{ padding: '20px' }}>
                    <Button
                        onClick={() => {
                            setRiskAnalysisOpen(false);
                            setWeatherData(null);
                        }}
                        variant="contained"
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            textTransform: 'none',
                            backgroundColor: '#57C09A',
                            '&:hover': { backgroundColor: '#4fcda2' }
                        }}
                    >
                        Zamknij
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog ustawień gołębnika */}
            <Dialog open={homeLocationDialogOpen} onClose={() => setHomeLocationDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '24px', color: '#478E95' }}>
                    Ustawienia gołębnika
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
                        <TextField
                            fullWidth
                            label="Nazwa gołębnika"
                            value={homeLocationName}
                            onChange={(e) => setHomeLocationName(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                        />
                        <TextField
                            fullWidth
                            label="Wyszukaj lokalizację"
                            placeholder="Wpisz caly adres, np. Lodz, Piotrkowska 120"
                            value={homeLocationSearch}
                            onChange={(e) => setHomeLocationSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && homeLocationSearch.trim()) {
                                    handleGeocode(homeLocationSearch.trim());
                                }
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                        />
                        <Button
                            variant="outlined"
                            onClick={() => {
                                if (homeLocationSearch.trim()) {
                                    handleGeocode(homeLocationSearch.trim());
                                }
                            }}
                            sx={{ width: 'fit-content', fontFamily: "'Poppins', sans-serif", textTransform: 'none' }}
                        >
                            Znajdz na mapie
                        </Button>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Szerokość geograficzna"
                                value={homeLocation.lat}
                                onChange={(e) => setHomeLocation({...homeLocation, lat: parseFloat(e.target.value) || 0})}
                                sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                            />
                            <TextField
                                label="Długość geograficzna"
                                value={homeLocation.lng}
                                onChange={(e) => setHomeLocation({...homeLocation, lng: parseFloat(e.target.value) || 0})}
                                sx={{ '& .MuiOutlinedInput-root': { fontFamily: "'Poppins', sans-serif" } }}
                            />
                        </Box>
                        <Box sx={{ height: '400px', marginTop: 2, position: 'relative', border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                            <MapContainer
                                center={[homeLocation.lat, homeLocation.lng]}
                                zoom={10}
                                style={{ height: '100%', width: '100%' }}
                            >
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                <MapClickHandler onClick={(e) => {
                                    setHomeLocation({ lat: e.latlng.lat, lng: e.latlng.lng });
                                }} />
                                <Marker position={[homeLocation.lat, homeLocation.lng]}>
                                    <Popup>{homeLocationName}</Popup>
                                </Marker>
                            </MapContainer>
                        </Box>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#707070' }}>
                            Kliknij na mapie, aby ustawić lokalizację gołębnika. Ta lokalizacja będzie używana do obliczania dystansów i analizy lotów.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ padding: '20px' }}>
                    <Button
                        onClick={() => setHomeLocationDialogOpen(false)}
                        sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none', color: '#707070' }}
                    >
                        Anuluj
                    </Button>
                    <Button
                        onClick={saveHomeLocation}
                        variant="contained"
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            textTransform: 'none',
                            backgroundColor: '#57C09A',
                            '&:hover': { backgroundColor: '#4fcda2' }
                        }}
                    >
                        Zapisz jako domyślny
                    </Button>
                </DialogActions>
            </Dialog>

        </div>
    );
};

export default Flights;
